import { ApolloClient } from '@apollo/client';

import {
  parseTableSettings,
  stringifyTableSettings,
  TableSettingFilter,
} from '../../make-table-settings';

import {
  ERP_FILTER_MAPPINGS,
  POSITIONS_FILTER_MAPPINGS,
} from './constants/erp-positions-filter-mapping';

type ResultFilter = {
  id: string;
  value: unknown;
};

const extractFiltersFromUrl = (): ResultFilter[] => {
  const settings = new URLSearchParams(window.location.search).get('settings');
  const settingsObject = settings ? parseTableSettings(settings) : undefined;
  return settingsObject?.filter ?? [];
};

const buildSearchParams = (filters: TableSettingFilter[]): string => {
  const params = new URLSearchParams();
  params.set('settings', stringifyTableSettings({ filter: filters }));
  return params.toString();
};

export const getPositionSearch = (): string => {
  const originalFilters = extractFiltersFromUrl();
  const resultFilters: ResultFilter[] = [];

  for (const { sourceId, targetId, transform } of POSITIONS_FILTER_MAPPINGS) {
    const match = originalFilters.find((f) => f.id === sourceId);
    if (match) {
      resultFilters.push({
        id: targetId,
        value: transform ? transform(match.value as never) : match.value,
      });
    }
  }

  return buildSearchParams(resultFilters as TableSettingFilter[]);
};

export const getErpSearch = async (client: ApolloClient<object>) => {
  const originalFilters = extractFiltersFromUrl();

  const promises = ERP_FILTER_MAPPINGS.map(
    async ({ sourceId, targetId, query, buildVariables }) => {
      const match = originalFilters.find((f) => f.id === sourceId);
      if (!match) return null;

      if (!query || !buildVariables) {
        return { id: targetId, value: match.value };
      }

      const variables = buildVariables(
        Array.isArray(match.value) ? match.value : [match.value]
      );

      const response = await client.query({
        fetchPolicy: 'no-cache',
        query,
        variables: {
          filter: JSON.stringify(variables),
        },
      });

      const mappedData = Object.values(response.data).flatMap(
        (x: { data: object[] }) => x.data
      );

      return { id: targetId, value: mappedData };
    }
  );

  const resultFilters = (await Promise.all(promises)).filter(
    (item): item is ResultFilter => Boolean(item)
  );

  return buildSearchParams(resultFilters as TableSettingFilter[]);
};
